import { useLazyChildComment, useLazyComment } from "@/hooks/useComment";
import { useLike } from "@/hooks/useLike";
import { useLazyMeta, useMetaLikeAndComment } from "@/hooks/useMeta";
import useTranslation from "@/hooks/useTranslation";
import { CommentScope, ICommentContent } from "@/interfaces/comment.interface";
import { currentUserState } from "@/stores/common";
import { sanitize } from "@/utils/sanitize.util";
import { formatRelativeTime } from "@/utils/time.util";
import { Button, IconButton } from "@material-ui/core";
import FavoriteIcon from "@material-ui/icons/Favorite";
import FavoriteBorderIcon from "@material-ui/icons/FavoriteBorder";
import { Comment, Tooltip } from "antd";
import { css } from "emotion";
import dynamic from "next/dynamic";
import React, { useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import DeleteCommentDialog from "./DeleteCommentDialog";
import EditCommentDialog from "./EditCommentDialog";
import MyChildCommentList from "./MyChildCommentList";
import styles from "./styles.module.less";

const AvatarBundle = dynamic(() => import("./AvatarBundle"), { ssr: false });
const Editor = dynamic(() => import("../Editor"), { ssr: false });

interface IProps {
  comment: ICommentContent;
  fatherId: string;
  scope: CommentScope;
}

const MyComment: React.FC<IProps> = ({
  fatherId,
  comment: { content, updated_at, user, children, id, url },
  scope,
}) => {
  const { t } = useTranslation("common");
  const { alterLike, loading: loadingLike } = useLike(id);
  const { getMeta, loading: loadingMeta } = useLazyMeta(id, true);
  const { likedStatus, likedTimeCount } = useMetaLikeAndComment(id);
  const [visible, setVisible] = useState(false);
  const { loadingAdd, addComment } = useLazyChildComment(id, scope);
  const {
    deleteComment,
    loadingDelete,
    updateComment,
    loadingUpdate,
  } = useLazyComment(fatherId, scope);
  const like = async () => {
    await alterLike(true);
  };
  const disLike = async () => {
    await alterLike(false);
  };
  const termToShow = useMemo(() => {
    if (visible) {
      return t("comment.close-reply");
    }
    if (children?.count > 0) {
      return t("comment.show-reply", { count: children?.count });
    }
    return t("comment.reply");
  }, [visible, children]);
  const currentUser = useRecoilValue(currentUserState);
  const login = Boolean(currentUser.id);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);

  const actions = [
    <Tooltip
      key={`${id}-like`}
      title={likedStatus ? t("comment.dislike") : t("comment.like")}
    >
      <span>
        {likedStatus ? (
          <IconButton
            size="small"
            onClick={disLike}
            color="primary"
            disabled={loadingLike || !login || loadingMeta}
          >
            <FavoriteIcon />
          </IconButton>
        ) : (
          <IconButton
            size="small"
            onClick={like}
            disabled={loadingLike || !login || loadingMeta}
          >
            <FavoriteBorderIcon />
          </IconButton>
        )}
        <span className="comment-action">{likedTimeCount}</span>
      </span>
    </Tooltip>,
    <Button
      size="small"
      key={`${id}-reply-to`}
      onClick={() => setVisible(!visible)}
    >
      {termToShow}
    </Button>,
    currentUser.id === user?.id && (
      <Button
        size="small"
        key={`${id}-update`}
        onClick={() => setOpenEdit(true)}
      >
        {t("comment.update.itself")}
      </Button>
    ),
    currentUser.id === user?.id && (
      <Button
        size="small"
        key={`${id}-delete`}
        onClick={() => setOpenDelete(true)}
        disabled={loadingDelete}
      >
        {t("comment.delete.itself")}
      </Button>
    ),
  ];

  return (
    <>
      <Comment
        className={styles.comment}
        content={
          <div dangerouslySetInnerHTML={{ __html: sanitize(content) }} />
        }
        datetime={formatRelativeTime(updated_at)}
        avatar={
          <AvatarBundle username={user?.username} avatarUrl={user?.avatarUrl} />
        }
        author={user?.username}
        actions={actions}
      >
        <div
          className={css`
            max-height: ${visible ? "200rem" : "0"};
            overflow: hidden;
            opacity: ${visible ? "1" : "0"};
            transition: max-height 0.5s ease-out, opacity 0.5s ease-in-out;
            display: flex;
            flex-direction: column;
            gap: 1rem;
          `}
        >
          <MyChildCommentList parentId={id} scope={scope} />
          {login && (
            <Editor
              fatherId={fatherId}
              parentId={id}
              submitting={loadingAdd}
              handleSubmit={addComment}
              url={url}
            />
          )}
        </div>
      </Comment>
      <DeleteCommentDialog
        open={openDelete}
        handleClose={() => setOpenDelete(false)}
        content={content}
        handleDelete={() => deleteComment(id)}
      />
      <EditCommentDialog
        open={openEdit}
        handleClose={() => setOpenEdit(false)}
        content={content}
        loadingUpdate={loadingUpdate}
        id={id}
        handleUpdate={updateComment}
      />
    </>
  );
};

export default MyComment;
